import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { login as apiLogin, logout as apiLogout } from "../auth";
import { getAuthToken, getRole } from "../tokenService";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const navigate = useNavigate();

  // Memoized logout function
  const logout = useCallback(() => {
    apiLogout();
    setUser(null);
    navigate("/");
  }, [navigate]);

  // Initialize auth state - runs only once on mount
  useEffect(() => {
    const token = getAuthToken();
    const role = getRole();
    
    if (token && role) {
      setUser({ role });
      setLastActivity(Date.now());
    }
  }, []); // Empty dependency array means this runs only once

  // Session timeout handler - DISABLED (no automatic logout)
  // useEffect(() => {
  //   const checkActivity = () => {
  //     if (user && Date.now() - lastActivity > 1800000) { // 15 minutes
  //       logout();
  //     }
  //   };
  //
  //   // Check every minute
  //   const interval = setInterval(checkActivity, 60000);
  //   return () => clearInterval(interval);
  // }, [user, lastActivity, logout]); // Stable dependencies

  // Update activity function - memoized
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
  }, []);

  // Login function - memoized
  const login = useCallback(async (email, password) => {
    try {
      const data = await apiLogin(email, password);

      // If server indicates 2FA is required, do not set token yet
      if (data && data.requires_2fa) {
        return {
          requires_2fa: true,
          email: data.email,
          message: data.message || "Verification code sent"
        };
      }

      // Normal login success
      localStorage.setItem("token", data.access_token);
      localStorage.setItem("role", data.role);

      setUser({ role: data.role });
      setLastActivity(Date.now());

      return { role: data.role, success: true };
    } catch (error) {
      throw error;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, updateActivity, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);